import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  qualityAPI,
  Concession,
  ConcessionComment,
  ConcessionActionLog,
  ConcessionDocument,
  ConcessionMentionOptions,
} from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useIsSupplier, useIsCustomer } from '../../hooks/usePortalMode';
import { useAuth } from '../../contexts/AuthContext';
import { DialogState, ApprovalFormState } from './types';

export function useConcessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isSupplier = useIsSupplier();
  const isCustomer = useIsCustomer();
  const { user } = useAuth();

  const [concession, setConcession] = useState<Concession | null>(null);
  const [comments, setComments] = useState<ConcessionComment[]>([]);
  const [actionLogs, setActionLogs] = useState<ConcessionActionLog[]>([]);
  const [documents, setDocuments] = useState<ConcessionDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  // CR-6510: who this user may ask to look at a comment, and who they picked.
  const [mentionOptions, setMentionOptions] = useState<ConcessionMentionOptions | null>(null);
  const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([]);
  const [mentionedDepartments, setMentionedDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [dialogs, setDialogs] = useState<DialogState>({
    approve: false,
    reject: false,
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalForm, setApprovalForm] = useState<ApprovalFormState>({
    notes: '',
    quantityApproved: '',
    validUntil: '',
    qnMrbReference: '',
    approverName: '',
    approverPosition: '',
    approverDepartment: '',
    customerResponseDate: '',
  });

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [concessionRes, commentsRes, logsRes, docsRes] = await Promise.all([
        qualityAPI.getConcession(parseInt(id)),
        qualityAPI.getConcessionComments(parseInt(id)),
        qualityAPI.getConcessionActionLog(parseInt(id)),
        qualityAPI.getConcessionDocuments(parseInt(id)),
      ]);

      setConcession(concessionRes.data);
      setComments(commentsRes.data);
      setActionLogs(logsRes.data);
      setDocuments(docsRes.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load concession'));
    } finally {
      setLoading(false);
    }
  }, [id, isSupplier]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // CR-6510: the mention picker is only ever populated with the caller's own
  // colleagues — the endpoint decides that, not the client. Loaded separately
  // from the main fetch so a failure here never blocks the page.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    qualityAPI.getConcessionMentionOptions(parseInt(id))
      .then((res) => { if (!cancelled) setMentionOptions(res.data); })
      .catch(() => { if (!cancelled) setMentionOptions(null); });
    return () => { cancelled = true; };
  }, [id]);

  const handleSubmit = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.submitConcession(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkUnderReview = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.markConcessionUnderReview(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to mark under review'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.approveConcession(concession.id, {
        review_notes: approvalForm.notes,
        quantity_approved: approvalForm.quantityApproved ? parseFloat(approvalForm.quantityApproved) : undefined,  // CR-7262: decimals
        valid_until: approvalForm.validUntil || undefined,
        // CR-6510: omit blanks so the server falls back to the acting user's
        // own name/title/department and today's date.
        qn_mrb_reference: approvalForm.qnMrbReference || undefined,
        approver_name: approvalForm.approverName || undefined,
        approver_position: approvalForm.approverPosition || undefined,
        approver_department: approvalForm.approverDepartment || undefined,
        customer_response_date: approvalForm.customerResponseDate || undefined,
      });
      setDialogs(prev => ({ ...prev, approve: false }));
      setApprovalForm({
        notes: '', quantityApproved: '', validUntil: '',
        qnMrbReference: '', approverName: '', approverPosition: '',
        approverDepartment: '', customerResponseDate: '',
      });
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!concession || !rejectionReason) return;
    try {
      setActionLoading(true);
      await qualityAPI.rejectConcession(concession.id, rejectionReason);
      setDialogs(prev => ({ ...prev, reject: false }));
      setRejectionReason('');
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.closeConcession(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to close concession'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptRisk = async () => {
    if (!concession) return;
    try {
      setActionLoading(true);
      await qualityAPI.acceptConcessionRisk(concession.id);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to accept risk'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!concession || !newComment.trim()) return;
    try {
      setCommentLoading(true);
      await qualityAPI.addConcessionComment(
        concession.id, newComment, isInternalComment,
        {
          mentioned_user_ids: mentionedUserIds,
          mentioned_departments: mentionedDepartments,
        },
      );
      setNewComment('');
      setIsInternalComment(false);
      setMentionedUserIds([]);
      setMentionedDepartments([]);
      // Refresh comments
      const commentsRes = await qualityAPI.getConcessionComments(concession.id);
      setComments(commentsRes.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add comment'));
    } finally {
      setCommentLoading(false);
    }
  };

  // CR-6510: evidence, from either side. The upload posts multipart so the
  // bytes actually land — the endpoint previously accepted metadata only.
  const handleUploadDocument = async (file: File) => {
    if (!concession) return;
    try {
      setUploading(true);
      const form = new FormData();
      form.append('concession', String(concession.id));
      form.append('file', file);
      form.append('file_name', file.name);
      await qualityAPI.uploadConcessionDocument(form);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to upload attachment'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      setUploading(true);
      await qualityAPI.deleteConcessionDocument(documentId);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete attachment'));
    } finally {
      setUploading(false);
    }
  };

  return {
    // Navigation
    navigate,

    // User info
    isSupplier,
    isCustomer,

    // Loading states
    loading,
    error,
    actionLoading,
    commentLoading,

    // Data
    concession,
    comments,
    actionLogs,
    documents,
    uploading,
    currentUserId: user?.id,

    // Dialog state
    dialogs,
    setDialogs,
    rejectionReason,
    setRejectionReason,
    approvalForm,
    setApprovalForm,

    // Comment state
    newComment,
    setNewComment,
    isInternalComment,
    setIsInternalComment,
    mentionOptions,
    mentionedUserIds,
    setMentionedUserIds,
    mentionedDepartments,
    setMentionedDepartments,

    // Actions
    handleSubmit,
    handleMarkUnderReview,
    handleApprove,
    handleReject,
    handleClose,
    handleAcceptRisk,
    handleAddComment,
    handleUploadDocument,
    handleDeleteDocument,
  };
}
